/**
 * @author Nihar <nihar.baishyar@314ecorp.com>
 * @description utils for custom attributes
 */

import _ from 'lodash';
import { CUSTOM_ATTRIBUTE_FIELD_TYPE } from 'app-constants/admin/customAttribute';
import { FieldTypeConfigs } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { IFieldsData, QUERY_BUILDER_FILTER_TYPE } from 'lib/SearchQueryBuilder/config/entity.config';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';

import { CUSTOM_ATTRIBUTE_PREFIX } from 'utils/adfUtils';

// prepare initial model
const prepareInitialModel = (data?: CustomAttributes.IAllResponse): Record<keyof CustomAttributes.IFormFields, AnyType> => {
	const fieldType = data?.attrDef?.props?.fieldType || null;
	const defaultValue = data?.attrDef?.jsonschema?.default;
	let listItems = [];
	if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.list) {
		listItems = _.map(data?.attrDef?.jsonschema?.enum || [], (item) => ({
			name: item,
			isDefault: item === defaultValue,
			id: crypto.randomUUID(),
		}));
	} else if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.multiSelectList) {
		listItems = _.map(data?.attrDef?.jsonschema?.items?.enum || [], (item) => ({
			name: item,
			isDefault: _.includes(defaultValue || [], item),
			id: crypto.randomUUID(),
		}));
	}
	return {
		fieldName: data?.attrDef?.jsonschema?.title || '',
		entity: data?.entityType || null,
		description: data?.attrDef?.jsonschema?.description || '',
		fieldType,
		defaultBooleanValue: fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.boolean ? defaultValue || false : false,
		listItems,
	};
};

// get json schema template
// prettier-ignore
const getJsonSchemaTemplate = (formValues: CustomAttributes.IFormFields): AnyRecord => {
	/* eslint-disable */
	const fieldType= formValues.fieldType;
	const nonEmptyListItems = _.filter(formValues.listItems, (item) => !_.isEmpty(item.name.trim()));
	const defaultItem = _.find(nonEmptyListItems, (item) => item.isDefault);
	const schemaUrl = 'http://json-schema.org/draft/2020-12/schema';
	switch (fieldType) {
		case 'boolean':
			return {
				"$schema": schemaUrl,
				"title": formValues.fieldName,
				"description": formValues.description,
				"type": "boolean",
				"default": formValues.defaultBooleanValue,
			};
		case 'date':
			return {
				"$schema": schemaUrl,
				"title": formValues.fieldName,
				"description": formValues.description,
				"type": "string",
				"format": "date",
			};
		case 'list':
			return {
				"$schema": schemaUrl,
				"title": formValues.fieldName,
				"description": formValues.description,
				"type": "string",
				"enum": _.map(nonEmptyListItems, (item) => item.name),
				...(defaultItem && {
					"default": defaultItem?.name,
				})
			};
		case 'multi_select_list':
			return {
				"$schema": schemaUrl,
				"title": formValues.fieldName,
				"description": formValues.description,
				"type": "array",
				"items": {
					"type": "string",
					"enum": _.map(nonEmptyListItems, (item) => item.name),
				},
				"uniqueItems": true,
				"default": _.filter(nonEmptyListItems, (item) => item.isDefault).map((item) => item.name),
			};
	}
};

// prepare add attribute model
const prepareAddAttributeModel = (model: CustomAttributes.IFormFields, userId: string): CustomAttributes.IAddRequestModel => {
	return {
		entityType: model.entity,
		postCustomAttributeRequest: {
			attrDef: {
				jsonschema: getJsonSchemaTemplate(model),
				props: {
					fieldType: model?.fieldType,
				},
			},
			isActive: true,
			createdBy: userId,
			order: null,
		},
	};
};

// prepare update attribute model
const prepareUpdateAttributeModel = (
	model: CustomAttributes.IFormFields,
	data: CustomAttributes.IAllResponse,
): CustomAttributes.IPatchRequestModel => {
	return {
		entityType: model?.entity,
		attributeId: data?.attributeId,
		patchCustomAttributeRequest: {
			attrDef: {
				jsonschema: getJsonSchemaTemplate(model),
				props: {
					fieldType: model?.fieldType,
				},
			},
			versionId: data?.versionId,
		},
	};
};

// function to get query builder schema for custom attributes
const getQueryBuilderSchemaForAttributes = (attribute: CustomAttributes.IAllResponse): IFieldsData => {
	const fieldType = attribute?.attrDef?.props?.fieldType;
	const attributeKey = `${CUSTOM_ATTRIBUTE_PREFIX}${attribute?.attributeKey}`;
	if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.boolean) {
		return {
			ui_field_type: QUERY_BUILDER_FILTER_TYPE.select,
			field: attributeKey,
			searchOperator: OPERATORS.equal,
			display_name: attribute?.attrDef?.jsonschema?.title,
			options: [
				{ value: 'true', label: 'True' },
				{ value: 'false', label: 'False' },
			],
			custom_props: {
				placeholder: `Select ${attribute?.attrDef?.jsonschema?.title}`,
			},
		};
	}
	if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.date) {
		return {
			...FieldTypeConfigs.datePicker,
			field: attributeKey,
			display_name: attribute?.attrDef?.jsonschema?.title,
		};
	}
	if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.list) {
		return {
			...FieldTypeConfigs.multiselect,
			field: attributeKey,
			display_name: attribute?.attrDef?.jsonschema?.title,
			options: _.map(attribute?.attrDef?.jsonschema?.enum, (item) => ({
				value: item,
				label: item,
			})),
			custom_props: {
				placeholder: `Select ${attribute?.attrDef?.jsonschema?.title}`,
				mode: 'single',
			},
		};
	}
	if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.multiSelectList) {
		return {
			...FieldTypeConfigs.multiselect,
			field: attributeKey,
			display_name: attribute?.attrDef?.jsonschema?.title,
			options: _.map(attribute?.attrDef?.jsonschema?.items?.enum, (item) => ({
				value: item,
				label: item,
			})),
			custom_props: {
				placeholder: `Select ${attribute?.attrDef?.jsonschema?.title}`,
			},
		};
	}
	return null;
};

export {
	prepareInitialModel,
	getJsonSchemaTemplate,
	prepareAddAttributeModel,
	prepareUpdateAttributeModel,
	getQueryBuilderSchemaForAttributes,
};
